import { useCallback, useRef, useState } from 'react';
import { ReadOrganizationMembershipCache } from './organization-membership-cache';
import { WriteOrganizationMembershipCache } from './organization-membership-cache';
import type { Organization } from './types';

type UseLazyOrganizationListOptions = {
  userKey: string;
  fetchOrganizations: () => Promise<Organization[]>;
};

/**
 * Cache-first org membership list for the switcher.
 * Closed button shows selected name only (parent). Opening the menu:
 * 1) localStorage hit → paint immediately + background network refresh
 * 2) miss → loading state + network → write cache
 */
export function useLazyOrganizationList(options: UseLazyOrganizationListOptions): {
  organizations: Organization[];
  organizationsLoading: boolean;
  loadOrganizations: () => void;
} {
  // Seeded synchronously so the count is known before the first click, not one fetch later:
  // whether there is anything to switch between decides what the bar searches.
  const [organizations, setOrganizations] = useState<Organization[]>(() => {
    return ReadOrganizationMembershipCache(options.userKey) || [];
  });
  const [organizationsLoading, setOrganizationsLoading] = useState(false);
  const fetchInFlight = useRef(false);

  const loadOrganizations = useCallback(() => {
    let showLoading = true;
    if (organizations.length > 0) {
      showLoading = false;
    } else if (options.userKey.length > 0) {
      const cached = ReadOrganizationMembershipCache(options.userKey);
      if (cached && cached.length > 0) {
        setOrganizations(cached);
        showLoading = false;
      }
    }
    if (!fetchInFlight.current) {
      fetchInFlight.current = true;
      if (showLoading) {
        setOrganizationsLoading(true);
      }
      void (async () => {
        try {
          const list = await options.fetchOrganizations();
          if (Array.isArray(list)) {
            setOrganizations(list);
            if (options.userKey.length > 0) {
              WriteOrganizationMembershipCache(options.userKey, list);
            }
          }
        } catch {} // Keep the cached or empty list
        fetchInFlight.current = false;
        setOrganizationsLoading(false);
      })();
    }
  }, [organizations.length, options.userKey, options.fetchOrganizations]);

  return {
    organizations: organizations,
    organizationsLoading: organizationsLoading,
    loadOrganizations: loadOrganizations,
  };
}
